import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import { LuArrowLeft, LuSend, LuExternalLink, LuDownload } from 'react-icons/lu';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import ActivityTimeline from '../../../components/ActivityTimeline';
import { useAuth } from '../../../contexts/AuthContext';
import { hasBillingRole } from '../../../utils/roles';

const STATUS_BADGES = {
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const FEE_TYPE_LABELS = {
  storage_fee: 'Storage',
  handling_inbound: 'Inbound Handling',
  handling_outbound: 'Outbound Handling',
  receiving_pallet: 'Receiving - Pallet',
  receiving_carton: 'Receiving - Carton',
  serial_capture: 'Serial Number Capture',
  mac_capture: 'MAC Address Capture',
  relabel: 'Relabel Service',
  order_picking: 'Order Picking',
  picking_fee: 'Picking',
  packing_fee: 'Packing',
  shipping_fee: 'Shipping',
  asset_tagging: 'Asset Tagging',
  reboxing: 'Reboxing',
  kitting_fee: 'Kitting',
  assembly_fee: 'Assembly',
  returns_handling: 'Returns Handling',
  labour: 'Labour',
  insurance: 'Insurance'
};

const openPdfBlob = (blob) => {
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
};

const InvoiceDetail = () => {
  const { user } = useAuth();
  const canMutateBilling = hasBillingRole(user);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data: invoice, isLoading, isError } = useQuery(
    ['admin-invoice', id],
    () => adminAPI.getInvoice(id),
    { enabled: !!id }
  );

  const statusMutation = useMutation(
    (data) => adminAPI.updateInvoiceStatus(id, data),
    {
      onSuccess: () => {
        toast.success('Invoice status updated');
        queryClient.invalidateQueries(['admin-invoice', id]);
        setShowStatusUpdate(false);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to update status')
    }
  );

  const zohoSyncMutation = useMutation(
    () => adminAPI.syncInvoiceToZoho(id),
    {
      onSuccess: (res) => {
        if (res.data?.success) toast.success('Synced to Zoho Books');
        else if (res.data?.skipped) toast.info(res.data?.reason || 'Sync skipped');
        else toast.error(res.data?.error || res.data?.reason || 'Zoho sync failed');
      },
      onError: () => toast.error('Zoho sync failed')
    }
  );

  const zohoSendMutation = useMutation(
    () => adminAPI.sendInvoiceViaZoho(id),
    {
      onSuccess: (res) => {
        if (res.data?.success) toast.success('Invoice sent via Zoho Books');
        else if (res.data?.skipped) toast.info(res.data?.reason || 'Send skipped');
        else toast.error(res.data?.error || res.data?.reason || 'Failed to send invoice');
        queryClient.invalidateQueries(['admin-invoice', id]);
      },
      onError: () => toast.error('Failed to send invoice')
    }
  );

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await adminAPI.downloadInvoicePdf(id);
      openPdfBlob(response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to open invoice PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError || !invoice) return <ErrorAlert message="Invoice not found." />;

  const items = invoice.items || [];
  const lineItems = items.filter(i => i.fee_type !== 'tax');
  const taxItem = items.find(i => i.fee_type === 'tax');
  const subtotal = lineItems.reduce((sum, i) => sum + (parseFloat(i.amount || i.rate * i.quantity) || 0), 0);
  const zohoInvoiceId = invoice.externalId || invoice.external_id;
  const zohoInvoiceUrl = invoice.zohoInvoiceUrl || null;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/admin/invoices" className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
          <LuArrowLeft className="mr-1 h-4 w-4" /> Back to Invoices
        </Link>
        <div className="flex items-center space-x-2">
          <button type="button" onClick={handleDownloadPdf} disabled={pdfLoading}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200">
            <LuDownload className="h-3 w-3 mr-1" /> {pdfLoading ? 'Opening...' : 'PDF'}
          </button>
          {zohoInvoiceUrl && (
            <a
              href={zohoInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              <LuExternalLink className="h-3 w-3 mr-1" /> View in Zoho
            </a>
          )}
          {/* Billing mutations are role-gated on the backend (RF15);
              hiding them beats a guaranteed 403 toast. */}
          {canMutateBilling && (
            <>
              <button type="button" onClick={() => zohoSyncMutation.mutate()} disabled={zohoSyncMutation.isLoading}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">
                {zohoSyncMutation.isLoading ? 'Syncing...' : (zohoInvoiceId ? 'Re-sync to Zoho' : 'Sync to Zoho')}
              </button>
              {/* A cancelled invoice must never be emailed -- cleanup can
                  leave a cancelled row holding a live Zoho id while the
                  void is retried, and sending it would deliver an active
                  payment link. The backend rejects it too; hiding beats a
                  guaranteed error toast. Sync stays available: on a
                  cancelled invoice it retries the remote cleanup. */}
              {invoice.status !== 'cancelled' && (
                <button type="button" onClick={() => zohoSendMutation.mutate()} disabled={zohoSendMutation.isLoading}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                  <LuSend className="h-3 w-3 mr-1" /> {zohoSendMutation.isLoading ? 'Sending...' : 'Send Invoice'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {invoice.Company?.name || 'Unknown Company'}
                {invoice.Request && (
                  <span> — <Link to={`/admin/requests/${invoice.Request.id}`} className="text-indigo-600 hover:text-indigo-800">
                    View Request <LuExternalLink className="inline h-3 w-3" />
                  </Link></span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGES[invoice.status] || STATUS_BADGES.pending}`}>
                {invoice.status}
              </span>
              {/* Open with an empty selection: initializing to the
                  current status broke linked invoices whose status
                  (pending/sent/overdue) is not among the offered
                  options -- the select DISPLAYED "Paid" while state
                  held the absent value, so choosing Paid fired no
                  change and Update stayed disabled. */}
              {canMutateBilling && (
                <button type="button" onClick={() => { setNewStatus(''); setShowStatusUpdate(!showStatusUpdate); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline">
                  Change
                </button>
              )}
            </div>
          </div>

          {showStatusUpdate && (
            <div className="mt-3 flex items-center space-x-2">
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500">
                <option value="" disabled>Select status...</option>
                {/* Zoho-linked invoices only accept transitions Zoho can
                    mirror: paid (verified against Zoho) and cancelled
                    (pushes a void). pending/sent/overdue are
                    Zoho-controlled and sync automatically -- sending
                    goes through the Send via Zoho action instead. */}
                {zohoInvoiceId ? (
                  <>
                    <option value="paid">Paid (verify with Zoho)</option>
                    <option value="cancelled">Cancelled</option>
                  </>
                ) : (
                  <>
                    <option value="pending">Pending</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </>
                )}
              </select>
              <button type="button" onClick={() => statusMutation.mutate({ status: newStatus })}
                disabled={statusMutation.isLoading || !newStatus || newStatus === invoice.status}
                className="px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                Update
              </button>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-gray-200 text-sm">
          <div>
            <div className="text-gray-500">Issue Date</div>
            <div className="font-medium">{formatDate(invoice.issueDate)}</div>
          </div>
          <div>
            <div className="text-gray-500">Due Date</div>
            <div className="font-medium">{formatDate(invoice.dueDate)}</div>
          </div>
          <div>
            <div className="text-gray-500">Paid Date</div>
            <div className="font-medium">{invoice.paidDate ? formatDate(invoice.paidDate) : '-'}</div>
          </div>
          <div>
            <div className="text-gray-500">Total</div>
            <div className="text-lg font-bold text-gray-900">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</div>
          </div>
        </div>

        {/* Line Items */}
        <div className="px-6 py-4">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-xs font-medium text-gray-500 uppercase py-2">Description</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 w-20">Qty</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 w-24">Rate</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2 text-sm text-gray-900">
                    <div>{item.description || item.name || 'Service'}</div>
                    {item.fee_type && (
                      <div className="text-xs text-gray-400">
                        {FEE_TYPE_LABELS[item.fee_type] || item.fee_type.replace(/_/g, ' ')}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-600 text-right">{item.quantity || 1}</td>
                  <td className="py-2 text-sm text-gray-600 text-right">${parseFloat(item.rate || item.unit_price || 0).toFixed(2)}</td>
                  <td className="py-2 text-sm text-gray-900 text-right">${parseFloat(item.amount || (item.rate * item.quantity) || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <td colSpan="3" className="py-2 text-sm font-medium text-gray-600 text-right">Subtotal</td>
                <td className="py-2 text-sm font-medium text-gray-900 text-right">${subtotal.toFixed(2)}</td>
              </tr>
              {taxItem && (
                <tr>
                  <td colSpan="3" className="py-1 text-sm text-gray-500 text-right">{taxItem.description || 'Tax'}</td>
                  <td className="py-1 text-sm text-gray-700 text-right">${parseFloat(taxItem.amount || 0).toFixed(2)}</td>
                </tr>
              )}
              <tr className="border-t border-gray-300">
                <td colSpan="3" className="py-2 text-sm font-bold text-gray-900 text-right">Total</td>
                <td className="py-2 text-sm font-bold text-gray-900 text-right">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {lineItems.some((item) => ['serial_capture', 'mac_capture', 'relabel'].includes(item.fee_type)) && (
          <div className="px-6 py-4 border-t border-amber-200 bg-amber-50">
            <p className="text-sm text-amber-800">
              This invoice includes one-time inbound receipt services requested on the related ASN or manifest.
            </p>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="text-xs font-medium text-gray-500 uppercase mb-1">Notes</div>
            <p className="text-sm text-gray-700">{invoice.notes}</p>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <ActivityTimeline
          fetchFn={(params) => adminAPI.getEntityTimeline('Invoice', id, params)}
          entityType="Invoice"
          entityId={id}
          limit={25}
          title="Activity"
        />
      </div>
    </div>
  );
};

export default InvoiceDetail;
